import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// App-wide error boundary. A render-time exception anywhere below this used to
// unmount the whole React tree and leave a blank white page (and client-side
// navigation like the back button wouldn't recover it — only a full reload).
// This catches the error, keeps the app shell alive, and surfaces the message
// + stack so a stray malformed field (e.g. a freshly-scored question that's
// missing an expected array) is visible and recoverable instead of fatal.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the original message + component stack in the console for triage.
    console.error("[ErrorBoundary] Caught render error:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-lg w-full space-y-4 text-center">
          <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
          <h1 className="text-xl font-semibold">Something went wrong on this page</h1>
          <p className="text-sm text-muted-foreground">
            The app hit an unexpected error while rendering. Your work is saved — you
            can try again, or reload the page.
          </p>
          <pre className="text-left text-xs bg-muted rounded-md p-3 overflow-auto max-h-48 whitespace-pre-wrap">
            {error.message}
          </pre>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={this.handleReset}>
              Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      </div>
    );
  }
}
